import { useEffect, useMemo, useState } from 'react';
import { X, Minus, Plus, ArrowLeftRight } from 'lucide-react';
import { useCollection } from '../hooks/useCollection';
import { useSocial } from '../hooks/useSocial';
import * as SocialAPI from '../services/SocialAPI';
import { computeTradeables } from '../utils/tradeables';
import { ensureCardsLoaded, getCardInfo, setIdFromCardId } from '../utils/cardIndex';
import { getVariantConfig } from '../utils/cardVariants';
import { ensurePricesLoaded, variantToPrice, formatCurrency } from '../utils/portfolioValue';

const FALLBACK_IMG =
  'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDgiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCA0OCA2NCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ4IiBoZWlnaHQ9IjY0IiBmaWxsPSIjRjNGNEY2Ii8+CjxjaXJjbGUgY3g9IjI0IiBjeT0iMzIiIHI9IjEwIiBmaWxsPSIjRDFENURCIi8+Cjwvc3ZnPg==';

const rowKey = (r) => `${r.cardId}|${r.variant}`;

// One selectable card row with a qty stepper capped at tradeableQty
const PickRow = ({ row, picked, onChange }) => {
  const info = getCardInfo(row.cardId);
  const config = getVariantConfig(row.variant);
  const qty = picked || 0;
  const price = variantToPrice(row.cardId, row.variant);

  return (
    <div
      className={`bg-white border rounded-xl p-2.5 flex items-center gap-2.5 transition-colors ${
        qty > 0 ? 'border-navy-dark' : 'border-border'
      }`}
    >
      <img
        src={info?.image || FALLBACK_IMG}
        alt={info?.name || row.cardId}
        className="w-9 h-12 object-contain rounded flex-shrink-0"
        onError={(e) => {
          e.target.src = FALLBACK_IMG;
        }}
      />
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-semibold text-ink-primary truncate">
          {info?.name || row.cardId}
        </div>
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className={`${config.color} text-white text-[8px] font-bold rounded px-1 py-0.5`}>
            {config.short}
          </span>
          <span className="text-[10px] text-ink-secondary truncate">
            {info ? `${info.setName} #${info.number}` : row.variant} • up to ×{row.tradeableQty}
          </span>
          {price > 0 && <span className="text-[10px] text-ink-muted">≈ {formatCurrency(price)}</span>}
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <button
          onClick={() => onChange(Math.max(0, qty - 1))}
          disabled={qty === 0}
          className={`w-6 h-6 rounded-full flex items-center justify-center ${
            qty === 0 ? 'bg-border text-ink-faint cursor-not-allowed' : 'bg-danger-bg text-brick'
          }`}
        >
          <Minus size={11} />
        </button>
        <span className="w-5 text-center text-[13px] font-bold text-ink-primary">{qty}</span>
        <button
          onClick={() => onChange(Math.min(row.tradeableQty, qty + 1))}
          disabled={qty >= row.tradeableQty}
          className={`w-6 h-6 rounded-full flex items-center justify-center ${
            qty >= row.tradeableQty
              ? 'bg-border text-ink-faint cursor-not-allowed'
              : 'bg-success-bg text-success-green'
          }`}
        >
          <Plus size={11} />
        </button>
      </div>
    </div>
  );
};

// Full-screen proposal builder. Seeded via SocialContext.proposalSeed:
// { friend?: {userId, username}, item?: {cardId, variant} }
const TradeProposalBuilder = () => {
  const { collection, settings } = useCollection();
  const {
    proposalSeed,
    closeProposalBuilder,
    friends,
    tradeOverrides,
    createTrade
  } = useSocial();

  const threshold = Number.isInteger(settings.tradeThreshold) ? settings.tradeThreshold : 1;

  const [friendId, setFriendId] = useState(null);
  const [friendTradeables, setFriendTradeables] = useState(null);
  const [give, setGive] = useState({}); // rowKey -> qty
  const [get, setGet] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [, setTick] = useState(0);

  const open = proposalSeed !== null;
  const friend = friends.find((f) => f.userId === friendId) || null;

  const myRows = useMemo(
    () => computeTradeables(collection, tradeOverrides, threshold).filter((r) => r.tradeableQty > 0),
    [collection, tradeOverrides, threshold]
  );

  // Initialize from seed on open
  useEffect(() => {
    if (!open) return;
    setGive({});
    setGet({});
    setError('');
    setFriendTradeables(null);
    setFriendId(proposalSeed.friend?.userId ?? (friends.length === 1 ? friends[0].userId : null));
    if (proposalSeed.item) {
      setGive({ [`${proposalSeed.item.cardId}|${proposalSeed.item.variant}`]: 1 });
    }
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, proposalSeed]);

  // Hydrate my own rows (no-op when TradeablesManager already cached them)
  useEffect(() => {
    if (!open || myRows.length === 0) return;
    let cancelled = false;
    ensureCardsLoaded(myRows.map((r) => r.cardId)).then(() => {
      if (!cancelled) setTick((t) => t + 1);
    });
    return () => {
      cancelled = true;
    };
  }, [open, myRows]);

  // Load the chosen friend's tradeables
  useEffect(() => {
    if (!open || !friendId) return;
    let cancelled = false;
    setFriendTradeables(null);
    setGet({});
    SocialAPI.getFriendTradeables(friendId)
      .then(async (items) => {
        if (cancelled) return;
        setFriendTradeables(items);
        await Promise.all([
          ensureCardsLoaded(items.map((i) => i.cardId)),
          ensurePricesLoaded([
            ...new Set([...items.map((i) => setIdFromCardId(i.cardId)), ...myRows.map((r) => setIdFromCardId(r.cardId))])
          ])
        ]);
        if (!cancelled) setTick((t) => t + 1);
      })
      .catch((err) => {
        console.error('Failed to load friend tradeables:', err);
        if (!cancelled) setError("Couldn't load your friend's tradeables.");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, friendId]);

  if (!open) return null;

  const totalFor = (picks, rows) =>
    Object.entries(picks).reduce((sum, [key, qty]) => {
      const [cardId, variant] = key.split('|');
      return sum + qty * variantToPrice(cardId, variant);
    }, 0);

  const giveCount = Object.values(give).reduce((s, q) => s + q, 0);
  const getCount = Object.values(get).reduce((s, q) => s + q, 0);
  const giveValue = totalFor(give, myRows);
  const getValue = totalFor(get, friendTradeables || []);

  const buildItems = () => {
    const items = [];
    for (const [key, qty] of Object.entries(give)) {
      if (qty < 1) continue;
      const [cardId, variant] = key.split('|');
      const info = getCardInfo(cardId);
      items.push({
        direction: 'proposer_gives',
        cardId,
        variant,
        quantity: qty,
        cardName: info?.name || '',
        cardImage: info?.image || '',
        setName: info?.setName || '',
        cardNumber: info?.number || '',
        unitPrice: variantToPrice(cardId, variant) || null
      });
    }
    for (const [key, qty] of Object.entries(get)) {
      if (qty < 1) continue;
      const [cardId, variant] = key.split('|');
      const info = getCardInfo(cardId);
      items.push({
        direction: 'recipient_gives',
        cardId,
        variant,
        quantity: qty,
        cardName: info?.name || '',
        cardImage: info?.image || '',
        setName: info?.setName || '',
        cardNumber: info?.number || '',
        unitPrice: variantToPrice(cardId, variant) || null
      });
    }
    return items;
  };

  const handleSubmit = async () => {
    setError('');
    setSubmitting(true);
    try {
      await createTrade(friendId, buildItems());
      closeProposalBuilder();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = friendId && giveCount + getCount > 0 && !submitting;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 md:flex md:items-center md:justify-center md:p-6">
      <div className="bg-cream-bg h-full md:h-auto md:max-h-[90vh] md:max-w-3xl md:w-full md:rounded-2xl md:shadow-modal overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-border px-4 md:px-6 py-3.5 flex items-center justify-between">
          <h2 className="font-heading font-bold text-lg text-ink-primary flex items-center gap-2">
            <ArrowLeftRight size={18} className="text-navy-dark" />
            Propose a Trade
          </h2>
          <button
            onClick={closeProposalBuilder}
            className="w-8 h-8 rounded-full flex items-center justify-center text-ink-faint hover:text-ink-primary hover:bg-cream-bg"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-4 md:px-6 py-4">
          {/* Friend picker */}
          <label className="block text-[12px] font-semibold text-ink-primary mb-1.5">
            Trading with
          </label>
          <select
            value={friendId ?? ''}
            onChange={(e) => setFriendId(e.target.value ? Number(e.target.value) : null)}
            className="w-full md:max-w-xs bg-white border border-border rounded-xl px-3 py-2.5 text-sm mb-5 focus:outline-none focus:ring-2 focus:ring-yellow"
          >
            <option value="">Choose a friend...</option>
            {friends.map((f) => (
              <option key={f.userId} value={f.userId}>
                {f.username}
              </option>
            ))}
          </select>

          {error && (
            <div className="mb-4 px-3 py-2.5 bg-danger-bg border border-danger-border rounded-xl text-brick text-[13px]">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* You give */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[12px] font-semibold uppercase tracking-wide text-yellow-dark">
                  You give ({giveCount})
                </h3>
                {giveValue > 0 && (
                  <span className="text-[11px] text-ink-muted">≈ {formatCurrency(giveValue)}</span>
                )}
              </div>
              {myRows.length === 0 ? (
                <p className="text-[12px] text-ink-secondary bg-white border border-border rounded-xl p-3">
                  You have no tradeable duplicates right now.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {myRows.map((row) => (
                    <PickRow
                      key={rowKey(row)}
                      row={row}
                      picked={give[rowKey(row)]}
                      onChange={(qty) => setGive((prev) => ({ ...prev, [rowKey(row)]: qty }))}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* You get */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-[12px] font-semibold uppercase tracking-wide text-success-green">
                  You get ({getCount})
                </h3>
                {getValue > 0 && (
                  <span className="text-[11px] text-ink-muted">≈ {formatCurrency(getValue)}</span>
                )}
              </div>
              {!friendId ? (
                <p className="text-[12px] text-ink-secondary bg-white border border-border rounded-xl p-3">
                  Pick a friend to see what they're offering.
                </p>
              ) : friendTradeables === null && !error ? (
                <div className="flex justify-center py-6">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-navy-dark border-t-transparent"></div>
                </div>
              ) : friendTradeables && friendTradeables.length === 0 ? (
                <p className="text-[12px] text-ink-secondary bg-white border border-border rounded-xl p-3">
                  {friend?.username} has no duplicates offered right now.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {(friendTradeables || []).map((row) => (
                    <PickRow
                      key={rowKey(row)}
                      row={row}
                      picked={get[rowKey(row)]}
                      onChange={(qty) => setGet((prev) => ({ ...prev, [rowKey(row)]: qty }))}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-white border-t border-border px-4 md:px-6 py-3.5 flex items-center justify-between gap-3">
          <div className="text-[12px] text-ink-secondary min-w-0">
            {giveCount + getCount === 0
              ? 'Pick at least one card on either side'
              : `${giveCount} out • ${getCount} in${
                  giveValue > 0 || getValue > 0
                    ? ` (≈ ${formatCurrency(giveValue)} for ${formatCurrency(getValue)})`
                    : ''
                }`}
          </div>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`px-5 py-2.5 rounded-xl text-sm font-semibold flex-shrink-0 transition-colors ${
              canSubmit
                ? 'bg-navy-dark text-yellow hover:bg-navy-light'
                : 'bg-border text-ink-faint cursor-not-allowed'
            }`}
          >
            {submitting ? 'Sending...' : 'Send Proposal'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TradeProposalBuilder;
